import { resolveModelBriefStats } from './briefStats.js';

// Shared Edition → preview model (data only: articles, sections, webUrl, etc.).
// Subscriber layout/typography: src/components/brief/* (+ briefWebStyles.js).
// Email HTML: api/lib/emailTemplate.js (unchanged when you edit the web brief).
// Do NOT import this file from api/ in the client — use src/lib/briefModel.js.

export const DEFAULT_SITE_BASE_URL = 'https://hangarxdaily.com';

export const CATEGORY_ORDER = ['Unmanned', 'Airspace', 'Manned', 'AI', 'Manufacturing'];

export const PILLAR_TAGLINE = {
  Unmanned: 'Drones, attritable platforms, and the autonomy stack reshaping defense procurement.',
  Airspace: 'Airspace integration, surveillance networks, and the rules governing who flies where.',
  Manned: 'Crewed aviation, certification, and the operators flying the mission today.',
  AI: 'The software, models, and decision layers underwriting the rest of the brief.',
  Manufacturing: 'Industrial capacity, supply chain, and the hardware that ships.',
};

const EMAIL_PALETTE = {
  Unmanned: { bar: '#C8351A', accent: '#F08F36', track: '#FFF6E8', ink: '#8A2412' },
  Airspace: { bar: '#0369A1', accent: '#38BDF8', track: '#EFF6FF', ink: '#075985' },
  Manned: { bar: '#15803D', accent: '#4ADE80', track: '#F0FDF4', ink: '#166534' },
  AI: { bar: '#7C3AED', accent: '#A78BFA', track: '#F5F3FF', ink: '#5B21B6' },
  Manufacturing: { bar: '#B45309', accent: '#FCD34D', track: '#FFFBEB', ink: '#854D0E' },
};

const FALLBACK_PALETTE = {
  bar: '#525252',
  accent: '#A3A3A3',
  track: '#F5F5F5',
  ink: '#262626',
};

export function paletteFor(category) {
  return EMAIL_PALETTE[category] || FALLBACK_PALETTE;
}

export function articleAnchorId(id) {
  return id ? `article-${String(id)}` : '';
}

export function normalizeSourceUrl(url) {
  return String(url || '')
    .trim()
    .replace(/\/+$/, '');
}

/** Match edition article by source.url or source.canonicalUrl. */
export function findArticleIdBySourceUrl(articles, url) {
  const key = normalizeSourceUrl(url);
  if (!key) return null;
  for (const a of articles || []) {
    const candidates = [a.source?.canonicalUrl, a.source?.url].filter(Boolean);
    for (const c of candidates) {
      if (normalizeSourceUrl(c) === key) return a._id ? String(a._id) : null;
    }
  }
  return null;
}

const MAX_INTRO_LINKS = 6;

/** Resolve AI anchor_links [{ url, phrase }] → intro.links [{ phrase, articleId }]. */
export function resolveIntroLinksFromAnchorLinks(articles, anchorLinks) {
  if (!Array.isArray(anchorLinks)) return [];
  const out = [];
  for (const entry of anchorLinks.slice(0, MAX_INTRO_LINKS)) {
    const phrase = typeof entry?.phrase === 'string' ? entry.phrase.trim() : '';
    const url = typeof entry?.url === 'string' ? entry.url.trim() : '';
    if (!phrase || !url) continue;
    const articleId = findArticleIdBySourceUrl(articles, url);
    if (!articleId) continue;
    out.push({ phrase, articleId });
  }
  return out;
}

/**
 * Split summary into text/link segments for Daily Summary rendering.
 * Skips phrases not found in summary and overlapping matches.
 */
export function linkifySummary(summary, links, articles) {
  const text = String(summary || '');
  if (!text) return [{ type: 'text', text: '' }];

  const articleById = new Map(
    (articles || []).map((a) => [String(a.articleId || a._id || ''), a])
  );

  const candidates = (links || [])
    .map((link) => {
      const phrase = typeof link?.phrase === 'string' ? link.phrase.trim() : '';
      const articleId = link?.articleId != null ? String(link.articleId) : '';
      if (!phrase || !articleId) return null;
      const idx = text.indexOf(phrase);
      if (idx === -1) return null;
      const article = articleById.get(articleId);
      return {
        phrase,
        start: idx,
        end: idx + phrase.length,
        articleId,
        category: article?.category || '',
        href: article?.webUrl || (articleId ? `#${articleAnchorId(articleId)}` : ''),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start || b.phrase.length - a.phrase.length);

  const picked = [];
  let cursor = 0;
  for (const c of candidates) {
    if (c.start < cursor) continue;
    picked.push(c);
    cursor = c.end;
  }

  if (picked.length === 0) return [{ type: 'text', text }];

  const segments = [];
  let pos = 0;
  for (const c of picked) {
    if (c.start > pos) segments.push({ type: 'text', text: text.slice(pos, c.start) });
    segments.push({
      type: 'link',
      text: c.phrase,
      articleId: c.articleId,
      category: c.category,
      href: c.href,
    });
    pos = c.end;
  }
  if (pos < text.length) segments.push({ type: 'text', text: text.slice(pos) });
  return segments;
}

export function pillarSlug(category) {
  return String(category || '')
    .toLowerCase()
    .trim();
}

function categoryIndex(category) {
  const i = CATEGORY_ORDER.indexOf(category);
  return i === -1 ? CATEGORY_ORDER.length : i;
}

export function stripSubjectPrefix(s) {
  if (!s) return '';
  return s.replace(/^\s*HXD\s*[—\-:]\s*/i, '').trim();
}

export function firstSentence(text) {
  const s = String(text || '').trim();
  if (!s) return '';
  const m = s.match(/^[^.!?]+[.!?]/);
  const out = (m ? m[0] : s).trim();
  return out.length > 200 ? `${out.slice(0, 197)}…` : out;
}

function formatBriefDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatBriefDateLong(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  const dt = new Date(Date.UTC(y, m - 1, d));
  const weekday = dt.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long' });
  const monthDay = dt.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return `${weekday} · ${monthDay}`;
}

export function buildPreviewModel(edition, opts = {}) {
  const isoDate = edition?.isoDate || '';
  const briefDate = formatBriefDate(isoDate);
  const briefDateLong = formatBriefDateLong(isoDate);
  const siteBaseUrl = (opts.siteBaseUrl || DEFAULT_SITE_BASE_URL).replace(/\/+$/, '');
  const briefPathRaw = (opts.briefPath || '/').replace(/\/+$/, '');
  const briefPath = briefPathRaw && briefPathRaw !== '/' ? briefPathRaw : '';

  const articles = (edition?.articles || [])
    .filter((a) => !!a)
    .sort((a, b) => {
      const ca = categoryIndex(a.category);
      const cb = categoryIndex(b.category);
      if (ca !== cb) return ca - cb;
      return (a.order || 0) - (b.order || 0);
    })
    .map((a, i) => {
      const articleId = a._id ? String(a._id) : '';
      const pathSuffix = briefPath
        ? briefPath.startsWith('/')
          ? briefPath
          : `/${briefPath}`
        : '';
      const webUrl = articleId
        ? `${siteBaseUrl}${pathSuffix}#${articleAnchorId(articleId)}`
        : '';
      return {
        number: String(i + 1).padStart(2, '0'),
        articleId,
        webUrl,
        title: a.headline || a.source?.originalTitle || 'Untitled',
        source: a.source?.displayName || a.source?.domain || '',
        category: a.category || '—',
        takeaway: a.keyTakeaway || '',
        summary: a.summary || '',
        url: a.source?.canonicalUrl || a.source?.url || '',
        keyClaims: Array.isArray(a.keyClaims) ? a.keyClaims : [],
        imageUrl: a.imageUrl || '',
        imageAlt: a.imageAlt || '',
      };
    });

  const sections = [];
  for (const cat of CATEGORY_ORDER) {
    const list = articles.filter((a) => a.category === cat);
    if (list.length === 0) continue;
    sections.push({ category: cat, tagline: PILLAR_TAGLINE[cat], articles: list });
  }
  const unmapped = articles.filter((a) => !CATEGORY_ORDER.includes(a.category));
  if (unmapped.length) {
    sections.push({ category: 'Other', tagline: '', articles: unmapped });
  }

  const introLinks = Array.isArray(edition?.intro?.links) ? edition.intro.links : [];
  const overview = {
    thesis: edition?.intro?.summary || '',
    bullets: Array.isArray(edition?.intro?.bullets) ? edition.intro.bullets : [],
    anchorUrls: Array.isArray(edition?.intro?.anchorUrls) ? edition.intro.anchorUrls : [],
    links: introLinks.map((l) => ({
      phrase: l.phrase || '',
      articleId: l.articleId ? String(l.articleId) : '',
    })),
    segments: linkifySummary(
      edition?.intro?.summary || '',
      introLinks,
      articles
    ),
  };

  const marketPulse = edition?.marketPulse || {};
  const tickers = []
    .concat(Array.isArray(marketPulse.indices) ? marketPulse.indices : [])
    .concat(
      (Array.isArray(marketPulse.stocks) ? marketPulse.stocks : []).map((s) => ({
        label: s.ticker ? (s.name ? `${s.ticker} · ${s.name}` : s.ticker) : s.name,
        value: s.price,
        change: s.change,
        direction: s.direction,
      }))
    )
    .filter((t) => t && (t.label || t.value))
    .slice(0, 4);
  const pulseAsOf = marketPulse.asOf || '';

  const subjectLine =
    edition?.subjectLine || (briefDate ? `HXD Morning Brief — ${briefDate}` : 'HXD Morning Brief');
  const previewText =
    edition?.previewText ||
    firstSentence(overview.thesis) ||
    'Aerospace, autonomy, and defense — what moved overnight.';

  const issueHeadline = stripSubjectPrefix(subjectLine) || 'Today’s Morning Brief';
  const issueSubhead = previewText;

  const heroImage =
    edition?.heroImage && edition.heroImage.url
      ? {
          url: edition.heroImage.url,
          alt: edition.heroImage.alt || '',
        }
      : null;

  return {
    isoDate,
    briefDate,
    briefDateLong,
    articles,
    sections,
    overview,
    siteBaseUrl,
    onlineUrl: `${siteBaseUrl}${briefPath ? (briefPath.startsWith('/') ? briefPath : `/${briefPath}`) : ''}`,
    subjectLine,
    previewText,
    issueHeadline,
    issueSubhead,
    marketPulse: { tickers, asOf: pulseAsOf },
    heroImage,
    briefStats: resolveModelBriefStats(edition, articles),
  };
}
